/**
 * Payroll Setup — everything that decides what a payable day is, and the two figures that are
 * the same for the whole organisation:
 *
 *   1. The month's total working days (calendar days minus Sundays, overridable).
 *   2. Which day types earn a payable day at all, and what a day of each is worth.
 *   3. The holiday calendar — dated, org-wide. Holidays used to be a number typed into every
 *      employee's row one at a time, which is the wrong shape for a day the whole organisation
 *      has off; declared here, the server works out per employee who it actually earns a day
 *      for (a Sunday, a day they worked through, or a day before they joined earns nothing).
 *   4. AI model error for every employee at once — for the day a camera or the ingest pipeline
 *      was down and nobody on site got recorded. Per-employee cells stay editable for the
 *      exceptions; this is just the figure they start from.
 *
 * All of it lives behind a modal rather than in the filter bar because it re-values the whole
 * sheet: a stray keystroke on a filter-bar control would silently change every row's pay.
 * Nothing here is applied until Save — including adding and removing holidays, so a mis-click
 * on a delete button is not a change to what people are paid.
 *
 * LAYOUT. Four sections, one visible at a time, rather than one long scroll: each is a separate
 * decision, and stacked they were a page of small print you had to scroll past to reach Save.
 * Three things stay pinned outside the scroll area so switching section never hides them —
 *
 *   · the payable formula, which is the single sentence describing the effect of everything here;
 *   · the list of pending changes, because a change made on one section must stay visible from
 *     the others (the whole risk of tabs is a forgotten edit behind one of them);
 *   · Save and Cancel, which used to scroll off the bottom behind a long holiday list.
 *
 * Closing with unsaved edits asks first. Every edit here is worth several minutes of an
 * operator's attention and a mis-aimed click on the backdrop would drop the lot.
 */
import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  XMarkIcon, ArrowPathIcon, PlusIcon, TrashIcon,
  ExclamationTriangleIcon, ChevronDownIcon, LockClosedIcon, PencilIcon,
} from '@heroicons/react/24/outline';
import { BTN_DANGER, BTN_ICON, BTN_PRIMARY, BTN_SECONDARY, BTN_SECONDARY_SM } from '../../components/common/ui';

export interface PayrollComponent {
  key: string;
  field: string;
  /** The component's default day credit, used when it has no configurable one. */
  weight: number;
  setting: string;
  /** Settings key holding the administrator's chosen credit, on the components that allow one. */
  weight_setting?: string | null;
  label: string;
  hint?: string;
  /**
   * The count is derived rather than typed in, so the sheet shows it but offers no input.
   * True for holiday, which comes from the calendar below.
   */
  readonly?: boolean;
}

/** One declared holiday. `counted` is false on a Sunday — see the Holidays section. */
export interface PayrollHoliday {
  holiday_date: string;
  name: string;
  counted?: boolean;
  updated_by_name?: string | null;
}

/**
 * The payroll setup row as the API returns it: the `count_*` switches, the `weight_*` day
 * credits, and the day-length thresholds in minutes. Mixed value types, hence the index
 * signature.
 */
export interface PayrollSettings {
  [key: string]: boolean | number | string | null | undefined;
  full_day_minutes?: number;
  half_day_minutes?: number;
}

/** The credits a weighted component can be set to, when the API doesn't say. */
export const DEFAULT_WEIGHT_OPTIONS = [1, 0.5];

/**
 * What one day of this component is worth right now: the configured credit where the
 * component has one, its default otherwise.
 *
 * Exported because the payroll table computes the same figure for its live payable column —
 * two copies of this would let the sheet and the setup modal disagree.
 */
export function effectiveWeight(c: PayrollComponent, settings: PayrollSettings): number {
  const configured = c.weight_setting ? settings[c.weight_setting] : undefined;
  return typeof configured === 'number' ? configured : c.weight;
}

/** "Half day" → "½ × half day" when a day of it isn't worth a whole payable day. */
export function weightedLabel(c: PayrollComponent, weight: number): string {
  const label = c.label.toLowerCase();
  return weight === 1 ? label : `${weight === 0.5 ? '½' : weight} × ${label}`;
}

export interface PayrollSetupModalProps {
  month: string;
  /** Working days currently in force (override if set, else derived). */
  workingDays: number;
  /** What calendar-minus-Sundays gives, for the "Auto" reset. */
  autoWorkingDays: number;
  /** 'override' once an administrator has set a number for this month. */
  workingDaysSource?: string;
  daysInMonth: number;
  components: PayrollComponent[];
  settings: PayrollSettings;
  /** Day credits the API accepts; falls back to whole / half day. */
  weightOptions?: number[];
  /** Holidays already declared inside the viewed month. */
  holidays?: PayrollHoliday[];
  /** How many employees a bulk AI-model-error apply would write to — stated before it happens. */
  employeeCount?: number;
  /**
   * How many currently have a non-zero AI model error. Lets the clear option say what it is about
   * to remove, which is the difference between revoking a bulk apply and wiping figures someone
   * entered by hand.
   */
  aiErrorEmployeeCount?: number;
  /**
   * False for a role that may read the setup but not change it. The controls go read-only and
   * Save disappears, rather than the operator filling the modal in and being refused at the end —
   * the permission is known before a single field is touched, so it should be said then.
   */
  canEdit?: boolean;
  /**
   * Persists the change. Receives only what the operator actually touched, so an untouched
   * working-day count is never rewritten as an override.
   */
  onSave: (change: {
    settings?: Record<string, boolean | number>;
    workingDays?: number | null;
    dayMinutes?: { full_day_minutes: number; half_day_minutes: number };
    /** Holiday calendar edits, as a diff — the API declares and withdraws one date at a time. */
    holidays?: { added: { date: string; name: string }[]; removed: string[] };
    /** Counts to stamp onto every active employee for the month. */
    bulkAdjustments?: Record<string, number>;
  }) => Promise<void>;
  onClose: () => void;
}

/** Minutes ⇄ hours, so the modal can talk in hours while the API stays in minutes. */
const toHours = (minutes: number) => String(Math.round((minutes / 60) * 100) / 100);
const toMinutes = (hours: string) => Math.round(Number(hours) * 60);

/** Hours must land on a whole minute and stay inside a day. */
function validHours(raw: string): boolean {
  if (raw.trim() === '') return false;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || n > 24) return false;
  return Number.isInteger(Math.round(n * 60)) && Math.abs(n * 60 - Math.round(n * 60)) < 1e-9;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Single letters for the calendar grid's header — seven columns don't have room for "Mon". */
const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Date-string helpers, all built from the string's own parts through Date.UTC rather than by
 * parsing it. `new Date('2026-08-15')` is UTC midnight, which is the previous day in any
 * timezone behind UTC — the day-shift that has bitten work dates here before. The weekday is
 * what decides whether a holiday earns anything, so getting it wrong by a day would be a
 * payroll error, not a display glitch.
 */
function utcParts(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
const isSunday = (date: string) => utcParts(date).getUTCDay() === 0;
/** "15 Aug (Sat)" — the weekday is shown because a Sunday holiday earns nobody a day. */
function fmtHolidayDate(date: string): string {
  const dt = utcParts(date);
  return `${dt.getUTCDate()} ${MONTH_ABBR[dt.getUTCMonth()]} (${WEEKDAYS[dt.getUTCDay()]})`;
}
/** "August 2026" for the modal's own headings — "2026-08" is a machine's way of saying it. */
function fmtMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return MONTH_NAMES[m - 1] ? `${MONTH_NAMES[m - 1]} ${y}` : month;
}

/** Day counts are whole or half days, within the month's working days. */
function validDayCount(raw: string, max: number): boolean {
  if (raw.trim() === '') return false;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= max && Math.round(n * 2) === n * 2;
}

/** The component whose count this modal sets for everyone at once. */
const BULK_FIELD = 'ai_error_days';

/**
 * Stable empty default for `holidays`. A literal `= []` in the destructuring would be a fresh
 * array on every render, and `holidays` is a dependency of the draft-reset effect — which would
 * reset the draft, re-render, and go round again forever for any caller that omits the prop.
 */
const NO_HOLIDAYS: PayrollHoliday[] = [];

/** The sections, in the order they are worked through. */
const TABS = [
  { key: 'month', label: 'Month & day length' },
  { key: 'holidays', label: 'Holidays' },
  { key: 'counts', label: 'What counts' },
  { key: 'bulk', label: 'Bulk AI error' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** Section heading + one line of explanation, uniform across the four sections. */
function SectionHead({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
      <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}

export default function PayrollSetupModal({
  month, workingDays, autoWorkingDays, workingDaysSource, daysInMonth,
  components, settings, weightOptions, holidays = NO_HOLIDAYS,
  employeeCount = 0, aiErrorEmployeeCount = 0, canEdit = true, onSave, onClose,
}: PayrollSetupModalProps) {
  const weights = weightOptions?.length ? weightOptions : DEFAULT_WEIGHT_OPTIONS;

  /** First and last day of the viewed month, bounding the holiday date picker. */
  const monthStart = `${month}-01`;
  const monthEnd = `${month}-${String(daysInMonth).padStart(2, '0')}`;

  /** Only the boolean switches — the thresholds are held separately, in hours. */
  const switchesOf = (s: PayrollSettings) =>
    components.reduce<Record<string, boolean>>((acc, c) => { acc[c.setting] = !!s[c.setting]; return acc; }, {});

  /** The chosen day credit per weighted component, keyed by its `weight_*` settings key. */
  const weightsOf = (s: PayrollSettings) =>
    components.reduce<Record<string, number>>((acc, c) => {
      if (c.weight_setting) acc[c.weight_setting] = effectiveWeight(c, s);
      return acc;
    }, {});

  const [tab, setTab] = useState<TabKey>('month');
  const [draftSettings, setDraftSettings] = useState<Record<string, boolean>>(() => switchesOf(settings));
  const [draftWeights, setDraftWeights] = useState<Record<string, number>>(() => weightsOf(settings));
  const [draftWorkingDays, setDraftWorkingDays] = useState<string>(String(workingDays ?? ''));
  const [fullHours, setFullHours] = useState<string>(toHours(settings.full_day_minutes ?? 480));
  const [halfHours, setHalfHours] = useState<string>(toHours(settings.half_day_minutes ?? 240));
  /**
   * Where the month's working-day count comes from, as a choice rather than as a number that
   * happens to match: 'derived' clears any override, 'custom' sends the typed figure. Typing the
   * same number the derivation produces is not the same intent as saying "follow the derivation
   * from now on", and only the latter survives a month with a different number of Sundays.
   */
  const [workingDaysMode, setWorkingDaysMode] =
    useState<'derived' | 'custom'>(workingDaysSource === 'override' ? 'custom' : 'derived');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Pending-change list expanded. Collapsed by default — it's a reassurance, not a step. */
  const [changesOpen, setChangesOpen] = useState(false);
  /** Set by a close attempt made with unsaved edits; the footer asks instead of closing. */
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  /**
   * The holiday list as the operator is editing it. Held as a draft like everything else here:
   * removing a holiday takes a payable day off every employee who had it, which is not something
   * a single click should commit.
   */
  const [draftHolidays, setDraftHolidays] = useState<PayrollHoliday[]>(() => [...holidays]);
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');
  /** Focused after a day is picked out of the calendar, so the name can just be typed. */
  const nameInputRef = useRef<HTMLInputElement>(null);

  /**
   * Bulk AI model error. Off unless deliberately switched on — it writes to every row.
   *
   * `bulkMode` is what makes the org-wide apply revocable: 'clear' sends zero to everyone, which
   * is the same write with a different number but has to be a named choice. Expecting an operator
   * to discover that typing 0 undoes it is how a mistaken apply becomes a manual repair job.
   */
  const [bulkOn, setBulkOn] = useState(false);
  const [bulkMode, setBulkMode] = useState<'set' | 'clear'>('set');
  const [bulkValue, setBulkValue] = useState('1');

  // Reopening on a different month must not show the previous month's draft. `holidays` is in
  // the deps for the same reason `settings` is: a refetch after a partial save must re-sync the
  // draft to what is actually stored, rather than leaving edits that already landed on screen
  // as if they were still pending.
  useEffect(() => {
    setDraftSettings(switchesOf(settings));
    setDraftWeights(weightsOf(settings));
    setDraftWorkingDays(String(workingDays ?? ''));
    setFullHours(toHours(settings.full_day_minutes ?? 480));
    setHalfHours(toHours(settings.half_day_minutes ?? 240));
    setWorkingDaysMode(workingDaysSource === 'override' ? 'custom' : 'derived');
    setDraftHolidays([...holidays]);
    setNewDate('');
    setNewName('');
    setBulkOn(false);
    setBulkMode('set');
    setError(null);
    setConfirmDiscard(false);
    setChangesOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, workingDays, month, holidays]);

  const parsedWorkingDays = Number(draftWorkingDays);
  const workingDaysValid =
    draftWorkingDays !== '' && Number.isInteger(parsedWorkingDays) &&
    parsedWorkingDays >= 0 && parsedWorkingDays <= daysInMonth;

  const hoursValid = validHours(fullHours) && validHours(halfHours);
  // Half must be strictly under full, or every long day would classify as a half day. The
  // backend enforces this too; checked here so the button explains itself before a round trip.
  const hoursOrdered = hoursValid && toMinutes(halfHours) < toMinutes(fullHours);

  const settingsChanged = components.some((c) => !!draftSettings[c.setting] !== !!settings[c.setting]);
  // A credit change on a component that is switched OFF still counts as a change worth saving:
  // it decides what that component will be worth the moment someone turns it on.
  const weightsChanged = components.some(
    (c) => !!c.weight_setting && draftWeights[c.weight_setting] !== effectiveWeight(c, settings),
  );
  const hoursChanged = hoursValid && (
    toMinutes(fullHours) !== (settings.full_day_minutes ?? 480) ||
    toMinutes(halfHours) !== (settings.half_day_minutes ?? 240)
  );
  const workingDaysChanged = workingDaysMode === 'derived'
    ? workingDaysSource === 'override'
    : workingDaysValid && parsedWorkingDays !== workingDays;

  /**
   * The holiday edits as the API takes them: dates to declare and dates to withdraw. A renamed
   * holiday lands in `added` — the declare endpoint upserts on the date, so a rename is one
   * write rather than a delete and an insert (which would briefly leave the day uncredited).
   */
  const holidayDiff = useMemo(() => {
    const original = new Map(holidays.map((h) => [h.holiday_date, h.name]));
    const draft = new Map(draftHolidays.map((h) => [h.holiday_date, h.name]));
    return {
      added: [...draft.entries()]
        .filter(([date, name]) => original.get(date) !== name)
        .map(([date, name]) => ({ date, name })),
      removed: [...original.keys()].filter((date) => !draft.has(date)),
    };
  }, [holidays, draftHolidays]);
  const holidaysChanged = holidayDiff.added.length > 0 || holidayDiff.removed.length > 0;

  /**
   * Clearing needs no input to be valid — it is always exactly zero — so only the 'set' mode is
   * gated on the typed value. Validated separately from `bulkValid` so switching to 'clear' can't
   * be blocked by a half-typed number left behind in the box.
   */
  const setValueValid = validDayCount(bulkValue, workingDays || daysInMonth);
  const bulkValid = bulkMode === 'clear' || setValueValid;
  const bulkDays = bulkMode === 'clear' ? 0 : Number(bulkValue);
  /**
   * A clear counts as a change even when nothing currently has a value: the operator asked for the
   * write, and the endpoint is idempotent. Suppressing it here would make the Save button dead for
   * no stated reason.
   */
  const bulkChanged = bulkOn && bulkValid;

  const dirty = settingsChanged || weightsChanged || workingDaysChanged || hoursChanged
    || holidaysChanged || bulkChanged;
  const canSubmit = canEdit && dirty && hoursOrdered
    && (workingDaysMode === 'derived' || workingDaysValid) && (!bulkOn || bulkValid);

  /** Escape and the backdrop go through the same guard as the ✕ — see `requestClose`. */
  const requestClose = () => {
    if (dirty && canEdit) { setConfirmDiscard(true); return; }
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // While the discard prompt is up, Escape backs out of the PROMPT. Making the second
      // Escape the one that throws the edits away would be a keystroke away from losing them.
      if (confirmDiscard) { setConfirmDiscard(false); return; }
      if (dirty && canEdit) { setConfirmDiscard(true); return; }
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, dirty, canEdit, confirmDiscard]);

  /**
   * A holiday can be declared on any date inside the month, and re-declared to rename it — the
   * endpoint upserts on the date, so a rename is a normal declare and needs no delete first.
   */
  const existingHoliday = draftHolidays.find((h) => h.holiday_date === newDate);
  const newDateInMonth = !!newDate && newDate >= monthStart && newDate <= monthEnd;
  const trimmedName = newName.trim();
  const isRename = !!existingHoliday;
  const nameUnchanged = !!existingHoliday && existingHoliday.name === trimmedName;
  const canApplyHoliday = canEdit && newDateInMonth && trimmedName.length > 0 && !nameUnchanged;

  const applyHoliday = () => {
    if (!canApplyHoliday) return;
    setDraftHolidays((list) => [
      ...list.filter((h) => h.holiday_date !== newDate),
      { holiday_date: newDate, name: trimmedName, counted: !isSunday(newDate) },
    ].sort((a, b) => a.holiday_date.localeCompare(b.holiday_date)));
    setNewDate('');
    setNewName('');
  };

  const removeHoliday = (date: string) => {
    setDraftHolidays((list) => list.filter((h) => h.holiday_date !== date));
    // Clear the form if it was pointed at the row just removed, so the button doesn't sit
    // there saying "Rename" for a holiday that no longer exists.
    if (newDate === date) { setNewDate(''); setNewName(''); }
  };

  /** Load a declared holiday into the form for renaming, from the list or the calendar. */
  const editHoliday = (h: PayrollHoliday) => {
    setNewDate(h.holiday_date);
    setNewName(h.name);
    nameInputRef.current?.focus();
  };

  /** Picking a day out of the calendar: a declared one loads for rename, a free one starts new. */
  const pickDay = (date: string) => {
    if (!canEdit) return;
    const found = draftHolidays.find((h) => h.holiday_date === date);
    if (found) { editHoliday(found); return; }
    setNewDate(date);
    setNewName('');
    nameInputRef.current?.focus();
  };

  /** Holidays that will actually earn a day — Sundays are listed but credit nobody. */
  const countedHolidays = draftHolidays.filter((h) => !isSunday(h.holiday_date)).length;

  /**
   * The month laid out as weeks, with leading blanks for the days before the 1st. A grid rather
   * than a date box alone: the question being answered is "which days of this month are off",
   * and that is a shape you read off a calendar — which Sundays already fall outside the working
   * days is visible at a glance instead of being checked one date at a time.
   */
  const calendarCells = useMemo(() => {
    const lead = utcParts(monthStart).getUTCDay();
    return [
      ...Array.from({ length: lead }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`),
    ];
  }, [month, monthStart, daysInMonth]);

  const holidayByDate = useMemo(
    () => new Map(draftHolidays.map((h) => [h.holiday_date, h])),
    [draftHolidays],
  );
  const pendingDates = useMemo(
    () => new Set(holidayDiff.added.map((a) => a.date)),
    [holidayDiff],
  );

  /**
   * The formula as currently configured, so the effect of a switch or a credit is visible
   * before saving. Reads the draft credits, not the saved ones.
   */
  const formula = useMemo(() => {
    const parts = components
      .filter((c) => draftSettings[c.setting])
      .map((c) => weightedLabel(c, c.weight_setting ? draftWeights[c.weight_setting] ?? c.weight : c.weight));
    return parts.length ? `Payable = ${parts.join(' + ')}` : 'Payable = 0 — nothing is counted';
  }, [components, draftSettings, draftWeights]);

  /**
   * Every pending change as one readable line, tagged with the section it was made on.
   *
   * This is what makes it safe to split the modal into sections: an edit behind a section you
   * are no longer looking at is still on screen, spelled out, next to the button that commits
   * it. Written from the same diffs the save payload is built from, so a line here and a write
   * there cannot come apart.
   */
  const pendingChanges = useMemo(() => {
    const out: { tab: TabKey; text: string; warn?: boolean }[] = [];
    if (workingDaysChanged) {
      out.push({
        tab: 'month',
        text: workingDaysMode === 'derived'
          ? `Working days follow the calendar again (${autoWorkingDays})`
          : `Working days set to ${parsedWorkingDays} (derived is ${autoWorkingDays})`,
      });
    }
    if (hoursChanged) {
      out.push({ tab: 'month', text: `A day counts as full at ${fullHours}h, half at ${halfHours}h` });
    }
    for (const c of components) {
      const on = !!draftSettings[c.setting];
      if (on !== !!settings[c.setting]) {
        out.push({
          tab: 'counts',
          text: `${c.label} ${on ? 'now earns payable days' : 'no longer earns payable days'}`,
          warn: !on,
        });
      }
      if (c.weight_setting && draftWeights[c.weight_setting] !== effectiveWeight(c, settings)) {
        const w = draftWeights[c.weight_setting];
        out.push({
          tab: 'counts',
          text: `A ${c.label.toLowerCase()} is worth ${w === 1 ? 'a whole payable day' : `${w} of a payable day`}`,
        });
      }
    }
    for (const a of holidayDiff.added) {
      const existed = holidays.some((h) => h.holiday_date === a.date);
      out.push({
        tab: 'holidays',
        text: `${existed ? 'Renamed' : 'Holiday'} ${fmtHolidayDate(a.date)} — ${a.name}`
          + (isSunday(a.date) ? ' (Sunday, earns nothing)' : ''),
      });
    }
    for (const date of holidayDiff.removed) {
      out.push({ tab: 'holidays', text: `Holiday withdrawn on ${fmtHolidayDate(date)}`, warn: true });
    }
    if (bulkChanged) {
      out.push({
        tab: 'bulk',
        text: bulkMode === 'clear'
          ? `AI model error cleared on ${employeeCount ? `all ${employeeCount} employees` : 'every employee'}`
          : `AI model error set to ${bulkDays} day${bulkDays === 1 ? '' : 's'} on ${employeeCount ? `all ${employeeCount} employees` : 'every employee'}`,
        warn: true,
      });
    }
    return out;
  }, [
    workingDaysChanged, workingDaysMode, autoWorkingDays, parsedWorkingDays, hoursChanged,
    fullHours, halfHours, components, draftSettings, draftWeights, settings, holidayDiff,
    holidays, bulkChanged, bulkMode, bulkDays, employeeCount,
  ]);

  /** Per-section markers on the tab strip: an unsaved edit behind it, or something unsavable. */
  const tabState: Record<TabKey, { dirty: boolean; invalid: boolean }> = {
    month: {
      dirty: workingDaysChanged || hoursChanged,
      invalid: (workingDaysMode === 'custom' && !workingDaysValid) || !hoursOrdered,
    },
    holidays: { dirty: holidaysChanged, invalid: false },
    counts: { dirty: settingsChanged || weightsChanged, invalid: false },
    bulk: { dirty: bulkChanged, invalid: bulkOn && !bulkValid },
  };

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        // Switches and credits are columns on the same row, so they go in one patch.
        ...(settingsChanged || weightsChanged ? { settings: { ...draftSettings, ...draftWeights } } : {}),
        ...(workingDaysChanged
          ? { workingDays: workingDaysMode === 'derived' ? null : parsedWorkingDays }
          : {}),
        ...(hoursChanged
          ? { dayMinutes: { full_day_minutes: toMinutes(fullHours), half_day_minutes: toMinutes(halfHours) } }
          : {}),
        ...(holidaysChanged ? { holidays: holidayDiff } : {}),
        ...(bulkChanged ? { bulkAdjustments: { [BULK_FIELD]: bulkDays } } : {}),
      });
      onClose();
    } catch (e: any) {
      // A 404 means the node's command-center service predates these endpoints, not that
      // something was missing in the request — say which, or the operator retries forever.
      setError(
        e?.response?.status === 404 && !e?.response?.data?.message
          ? "The command-center service does not have the payroll setup endpoints — it needs an updated build."
          : e?.response?.data?.message || e?.message || 'Could not save the payroll setup.',
      );
    } finally {
      setSaving(false);
    }
  };

  /** Shared input chrome, so a number box on one section matches one on another. */
  const inputBase = 'rounded-lg border bg-input px-2.5 py-1.5 text-[13px] text-foreground'
    + ' tabular-nums outline-none focus:border-brand focus:ring-2 focus:ring-brand-ring'
    + ' disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overlay-backdrop p-4"
      onClick={requestClose}
    >
      {/* Column layout: only the middle section scrolls, so the tab strip above and the formula,
          change list and Save below stay put however long the holiday list gets. */}
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Payroll setup"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
              Payroll setup
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {fmtMonth(month)}
              </span>
            </h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Decides what a payable day is. Nothing changes until you save.
            </p>
          </div>
          <button
            onClick={requestClose}
            aria-label="Close"
            className={BTN_ICON}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Read-only roles are told at the top, before filling anything in. */}
        {!canEdit && (
          <div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-5 py-2 text-[12px] text-muted-foreground">
            <LockClosedIcon className="h-3.5 w-3.5 shrink-0" />
            Your role can view the payroll setup but not change it.
          </div>
        )}

        {/* ── Section strip ──
            The dot is what keeps a split-up form honest: an edit made here and then left behind
            a switched section is still announced on the tab that holds it. */}
        <div role="tablist" aria-label="Payroll setup sections" className="flex shrink-0 gap-1 border-b border-border px-3 pt-2">
          {TABS.map((t) => {
            const state = tabState[t.key];
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className={`-mb-px flex items-center gap-1.5 rounded-t-lg border-b-2 px-3 py-2 text-[12px] font-medium transition-colors
                  ${active
                    ? 'border-brand text-foreground'
                    : 'border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground'}`}
              >
                {t.label}
                {t.key === 'holidays' && draftHolidays.length > 0 && (
                  <span className="rounded bg-muted px-1 text-[10px] font-semibold tabular-nums text-muted-foreground">
                    {draftHolidays.length}
                  </span>
                )}
                {/* A `title` on the icon itself is not a tooltip — an SVG needs a <title> child,
                    so the hint goes on a wrapping span. */}
                {state.invalid ? (
                  <span title="Something in this section can't be saved">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5 text-destructive" />
                  </span>
                ) : state.dirty ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-brand" title="Unsaved changes in this section" />
                ) : null}
              </button>
            );
          })}
        </div>

        <div role="tabpanel" aria-label={TABS.find((t) => t.key === tab)?.label} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {/* ── Month & day length ── */}
          {tab === 'month' && (
            <div className="space-y-6">
              <section className="space-y-2.5">
                <SectionHead title={`Total working days in ${fmtMonth(month)}`}>
                  {/* Public holidays used to be subtracted here. They are declared as dates on the
                      Holidays section instead, and earn a payable day — doing both would adjust
                      the same day twice. */}
                  The denominator for the month. Public holidays are declared on the Holidays
                  section and earn a payable day, so don't subtract them here as well.
                </SectionHead>

                {/* Two named choices rather than a number box with a reset button beside it:
                    "follow the calendar" and "this month is different" are different intents,
                    and only the first keeps working next month.

                    Radios in labels, not clickable cards: the second card holds a number input,
                    and a <button> may not contain one. A label ignores clicks that land on an
                    interactive descendant, so the box can still be typed into. */}
                <div className="grid gap-2 sm:grid-cols-2">
                  <label
                    className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors
                      ${canEdit ? 'cursor-pointer' : 'opacity-60'}
                      ${workingDaysMode === 'derived' ? 'border-brand bg-brand/5' : 'border-border hover:bg-muted/40'}`}
                  >
                    <input
                      type="radio"
                      name="working-days-mode"
                      checked={workingDaysMode === 'derived'}
                      disabled={!canEdit}
                      onChange={() => { setWorkingDaysMode('derived'); setDraftWorkingDays(String(autoWorkingDays)); }}
                      className="mt-1 h-3.5 w-3.5 shrink-0 accent-current text-brand"
                    />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium text-foreground">
                        From the calendar — {autoWorkingDays} days
                      </span>
                      <span className="block text-[11px] text-muted-foreground">
                        {daysInMonth} calendar days minus Sundays. Recalculated every month.
                      </span>
                    </span>
                  </label>

                  <label
                    className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors
                      ${canEdit ? 'cursor-pointer' : 'opacity-60'}
                      ${workingDaysMode === 'custom' ? 'border-brand bg-brand/5' : 'border-border hover:bg-muted/40'}`}
                  >
                    <input
                      type="radio"
                      name="working-days-mode"
                      checked={workingDaysMode === 'custom'}
                      disabled={!canEdit}
                      onChange={() => setWorkingDaysMode('custom')}
                      className="mt-1 h-3.5 w-3.5 shrink-0 accent-current text-brand"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium text-foreground">Set it for this month</span>
                      <span className="mt-1.5 flex items-center gap-1.5">
                        <input
                          type="number"
                          min={0}
                          max={daysInMonth}
                          step={1}
                          disabled={!canEdit}
                          value={workingDaysMode === 'derived' ? String(autoWorkingDays) : draftWorkingDays}
                          // Typing in the box picks the mode it belongs to, so the input is never
                          // a control that silently does nothing.
                          onChange={(e) => { setWorkingDaysMode('custom'); setDraftWorkingDays(e.target.value); }}
                          aria-label="Working days this month"
                          className={`w-16 ${inputBase} ${workingDaysMode === 'custom' && !workingDaysValid ? 'border-destructive' : 'border-border'}`}
                        />
                        <span className="text-[11px] text-muted-foreground">of {daysInMonth} days</span>
                      </span>
                    </span>
                  </label>
                </div>

                {workingDaysMode === 'custom' && !workingDaysValid && (
                  <p className="text-[11px] text-destructive">
                    Enter a whole number between 0 and {daysInMonth}.
                  </p>
                )}
                {workingDaysMode === 'derived' && workingDaysSource === 'override' && (
                  <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <ArrowPathIcon className="h-3 w-3" />
                    Saving drops the {workingDays}-day override set for this month.
                  </p>
                )}
              </section>

              <section className="space-y-2.5">
                <SectionHead title="Day length">
                  How much recorded time on site a day needs to count as a full or half day.
                  Applies to every month immediately, and to anyone without their own shift.
                </SectionHead>
                <div className="flex flex-wrap items-end gap-4">
                  <label className="flex flex-col gap-1">
                    <span className="text-[12px] text-muted-foreground">Full day at</span>
                    <span className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min={0.5}
                        max={24}
                        step={0.5}
                        disabled={!canEdit}
                        value={fullHours}
                        onChange={(e) => setFullHours(e.target.value)}
                        className={`w-20 ${inputBase} ${validHours(fullHours) ? 'border-border' : 'border-destructive'}`}
                      />
                      <span className="text-[12px] text-muted-foreground">hours</span>
                    </span>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[12px] text-muted-foreground">Half day at</span>
                    <span className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min={0.5}
                        max={24}
                        step={0.5}
                        disabled={!canEdit}
                        value={halfHours}
                        onChange={(e) => setHalfHours(e.target.value)}
                        className={`w-20 ${inputBase} ${validHours(halfHours) && hoursOrdered ? 'border-border' : 'border-destructive'}`}
                      />
                      <span className="text-[12px] text-muted-foreground">hours</span>
                    </span>
                  </label>
                  {/* The usual relation between the two, one click away — half a full day is what
                      "half day" means everywhere except where someone has deliberately set it
                      otherwise, and typing 4.5 to match a 9-hour day is an easy thing to forget. */}
                  {canEdit && validHours(fullHours) && toHours(toMinutes(fullHours) / 2) !== halfHours && (
                    <button
                      type="button"
                      onClick={() => setHalfHours(toHours(toMinutes(fullHours) / 2))}
                      className={`mb-1.5 ${BTN_SECONDARY_SM}`}
                    >
                      Make half = ½ of full ({toHours(toMinutes(fullHours) / 2)}h)
                    </button>
                  )}
                </div>
                {!hoursValid ? (
                  <p className="text-[11px] text-destructive">Enter hours between 0.5 and 24, on a whole minute.</p>
                ) : !hoursOrdered ? (
                  <p className="text-[11px] text-destructive">Half-day hours must be less than full-day hours.</p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    {halfHours}h or more is a half day, {fullHours}h or more is a full day. Under {halfHours}h
                    a day counts as partial, or as an only-in entry when no exit was seen.
                  </p>
                )}
              </section>
            </div>
          )}

          {/* ── Holiday calendar ──
              Dated and org-wide. The alternative it replaces was typing the same number into
              every employee's Holiday cell, which said nothing about WHICH days were holidays
              and had to be redone for anyone who joined later. */}
          {tab === 'holidays' && (
            <div className="space-y-3">
              <SectionHead title={`Holidays in ${fmtMonth(month)}`}>
                Paid non-working days for the whole organisation. Each earns every employee a
                holiday day — except anyone who was on site that day (the cameras already credited
                them) or who joined after it. A holiday on a Sunday earns nobody a day, since
                Sundays are already outside the working days.
              </SectionHead>

              <div className="grid gap-4 sm:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
                {/* Calendar: pick the day off the month rather than typing a date. Sundays are
                    visibly outside the working days, so declaring one is a deliberate act. */}
                <div className="rounded-xl border border-border p-3">
                  <div className="mb-1.5 grid grid-cols-7 gap-1">
                    {WEEKDAY_INITIALS.map((d, i) => (
                      <span
                        key={i}
                        className={`text-center text-[10px] font-semibold uppercase
                          ${i === 0 ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {calendarCells.map((date, i) => {
                      if (!date) return <span key={`pad-${i}`} />;
                      const dayNum = Number(date.slice(-2));
                      const declared = holidayByDate.get(date);
                      const sunday = isSunday(date);
                      const selected = newDate === date;
                      const pending = pendingDates.has(date);
                      return (
                        <button
                          key={date}
                          type="button"
                          disabled={!canEdit}
                          onClick={() => pickDay(date)}
                          aria-pressed={!!declared}
                          title={declared
                            ? `${declared.name} — ${sunday ? 'Sunday, earns nobody a day' : 'earns a holiday day'}. Click to rename.`
                            : sunday
                              ? `${fmtHolidayDate(date)} — Sunday, already outside the working days`
                              : `Declare a holiday on ${fmtHolidayDate(date)}`}
                          className={`relative h-8 rounded-md text-[12px] tabular-nums transition-colors disabled:cursor-not-allowed
                            ${declared
                              ? sunday
                                ? 'bg-muted text-muted-foreground ring-1 ring-inset ring-brand/40'
                                : 'bg-brand font-semibold text-white'
                              : sunday
                                ? 'text-muted-foreground/50 hover:bg-muted/60'
                                : 'text-foreground hover:bg-muted'}
                            ${selected ? 'ring-2 ring-brand ring-offset-1 ring-offset-card' : ''}`}
                        >
                          {dayNum}
                          {pending && (
                            <span
                              className={`absolute right-1 top-1 h-1.5 w-1.5 rounded-full ${declared && !sunday ? 'bg-white' : 'bg-brand'}`}
                              title="Unsaved"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <span className="h-2.5 w-2.5 rounded bg-brand" /> Counted
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="h-2.5 w-2.5 rounded bg-muted ring-1 ring-inset ring-brand/40" /> Sunday holiday — earns nothing
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Unsaved
                    </span>
                  </div>
                </div>

                <div className="min-w-0 space-y-2">
                  {/* Enter submits, so a calendar of holidays can be typed in without reaching for
                      the mouse between each one. */}
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="date"
                      value={newDate}
                      min={monthStart}
                      max={monthEnd}
                      disabled={!canEdit}
                      onChange={(e) => setNewDate(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyHoliday(); }}
                      aria-label="Holiday date"
                      className={`${inputBase} ${newDate && !newDateInMonth ? 'border-destructive' : 'border-border'}`}
                    />
                    <input
                      ref={nameInputRef}
                      type="text"
                      value={newName}
                      maxLength={120}
                      disabled={!canEdit}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyHoliday(); }}
                      placeholder="Holiday name"
                      aria-label="Holiday name"
                      className={`min-w-0 flex-1 border-border ${inputBase}`}
                    />
                    <button
                      type="button"
                      onClick={applyHoliday}
                      disabled={!canApplyHoliday}
                      className={BTN_SECONDARY}
                    >
                      {isRename
                        ? <><PencilIcon className="h-3.5 w-3.5" /> Rename</>
                        : <><PlusIcon className="h-3.5 w-3.5" /> Add</>}
                    </button>
                  </div>

                  {newDate && !newDateInMonth ? (
                    <p className="text-[11px] text-destructive">
                      Pick a date inside {fmtMonth(month)} — switch month on the payroll page to
                      declare one elsewhere.
                    </p>
                  ) : isRename && nameUnchanged ? (
                    <p className="text-[11px] text-muted-foreground">
                      {fmtHolidayDate(newDate)} is already declared as “{existingHoliday!.name}” — edit
                      the name to rename it.
                    </p>
                  ) : newDate && isSunday(newDate) ? (
                    <p className="text-[11px] text-warning">
                      {fmtHolidayDate(newDate)} is a Sunday — it can be declared for the record, but
                      earns nobody a payable day.
                    </p>
                  ) : null}

                  {draftHolidays.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-[12px] text-muted-foreground">
                      No holidays declared for {fmtMonth(month)}.
                      {canEdit && <> Pick a day on the calendar to declare one.</>}
                    </div>
                  ) : (
                    <ul className="divide-y divide-border rounded-lg border border-border">
                      {draftHolidays.map((h) => {
                        const sunday = isSunday(h.holiday_date);
                        const pending = pendingDates.has(h.holiday_date);
                        const selected = newDate === h.holiday_date;
                        return (
                          <li
                            key={h.holiday_date}
                            className={`flex items-center gap-2 px-2.5 py-2 ${selected ? 'bg-brand/5' : ''}`}
                          >
                            <button
                              type="button"
                              disabled={!canEdit}
                              onClick={() => editHoliday(h)}
                              title={canEdit ? 'Edit this holiday’s name' : h.name}
                              className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-default"
                            >
                              <span className="w-24 shrink-0 text-[12px] tabular-nums text-muted-foreground">
                                {fmtHolidayDate(h.holiday_date)}
                              </span>
                              <span className="min-w-0 flex-1 truncate text-[12px] text-foreground" title={h.name}>
                                {h.name}
                              </span>
                            </button>
                            {/* Stated on the row rather than left for the operator to discover from
                                a holiday count that didn't move. */}
                            {sunday && (
                              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                Sunday — not counted
                              </span>
                            )}
                            {pending && (
                              <span className="shrink-0 rounded bg-brand/10 px-1.5 py-0.5 text-[10px] font-medium text-brand">
                                unsaved
                              </span>
                            )}
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => removeHoliday(h.holiday_date)}
                                aria-label={`Remove ${h.name}`}
                                title="Remove this holiday — applied when you save"
                                className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <p className="text-[11px] text-muted-foreground">
                    {countedHolidays === 0
                      ? 'No holidays counted in this month.'
                      : `${countedHolidays} of ${draftHolidays.length} earn${countedHolidays === 1 ? 's' : ''} a payable day.`}
                    {/* A calendar full of holidays earning nothing because the component is
                        switched off is exactly the kind of silent zero worth saying out loud. */}
                    {!draftSettings.count_holiday && countedHolidays > 0 && (
                      <>
                        {' '}
                        <span className="text-warning">Holiday is switched off under “What counts”, so they earn no payable days.</span>
                        {' '}
                        <button
                          type="button"
                          onClick={() => setTab('counts')}
                          className="text-brand hover:underline"
                        >
                          Turn it on
                        </button>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Payable components ── */}
          {tab === 'counts' && (
            <div className="space-y-3">
              <SectionHead title="Which day types earn a payable day">
                Applies to every month, including ones already reported. Where a day type could
                reasonably be worth either, choose whether it earns a whole day or half a day.
              </SectionHead>
              <div className="divide-y divide-border rounded-xl border border-border">
                {components.map((c) => {
                  const on = !!draftSettings[c.setting];
                  const weight = c.weight_setting ? draftWeights[c.weight_setting] ?? c.weight : c.weight;
                  return (
                    <div key={c.setting} className="flex items-start gap-3 px-3 py-2.5">
                      <label className={`flex min-w-0 flex-1 items-start gap-2.5 ${canEdit ? 'cursor-pointer' : ''}`}>
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={!canEdit}
                          onChange={(e) => setDraftSettings((s) => ({ ...s, [c.setting]: e.target.checked }))}
                          className="mt-0.5 h-4 w-4 shrink-0 accent-current text-brand disabled:opacity-60"
                        />
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-foreground">
                            {c.label}
                            {/* Components with no picker still state their credit, so a half day
                                being worth half a day isn't left to be inferred. */}
                            {!c.weight_setting && c.weight !== 1 && (
                              <span className="rounded bg-muted px-1 text-[10px] font-semibold text-muted-foreground">
                                × {c.weight}
                              </span>
                            )}
                            {/* A count nobody can type in should say where it comes from, next to
                                the way to get there. */}
                            {c.readonly && (
                              <button
                                type="button"
                                onClick={() => setTab('holidays')}
                                className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:text-brand"
                              >
                                from the holiday calendar →
                              </button>
                            )}
                          </span>
                          {c.hint && <span className="mt-0.5 block text-[11px] leading-relaxed text-muted-foreground">{c.hint}</span>}
                        </span>
                      </label>

                      {/* Segmented picker rather than a select: two options, and the chosen
                          credit should be readable without opening anything. Stays enabled while
                          the component is off — it decides what turning it on will be worth —
                          but dims so it doesn't read as active policy. */}
                      {c.weight_setting && (
                        <span
                          role="group"
                          aria-label={`Day credit for ${c.label}`}
                          className={`mt-0.5 flex shrink-0 overflow-hidden rounded-lg border border-border ${on ? '' : 'opacity-50'}`}
                        >
                          {weights.map((w) => (
                            <button
                              key={w}
                              type="button"
                              disabled={!canEdit}
                              aria-pressed={weight === w}
                              onClick={() => setDraftWeights((s) => ({ ...s, [c.weight_setting as string]: w }))}
                              title={w === 1 ? 'Earns a whole payable day' : `Earns ${w} of a payable day`}
                              className={`px-2.5 py-1 text-[11px] font-medium tabular-nums transition-colors disabled:cursor-not-allowed
                                ${weight === w
                                  ? 'bg-brand text-white'
                                  : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                            >
                              {w === 0.5 ? '½×' : `${w}×`}
                            </button>
                          ))}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── AI model error for everyone ──
              The one hand-entered count that is routinely the same for the whole organisation:
              when a camera or the ingest pipeline was down for a day, nobody on site that day was
              recorded. Setting it a row at a time across a few hundred employees is how a payroll
              run gets abandoned half-finished. Per-employee cells stay editable afterwards.

              Applying and revoking are two named choices rather than one field you are expected to
              work out you can type 0 into. An org-wide apply that cannot be taken back the same way
              it was made is a trap: the operator who made it by mistake is left editing every row
              by hand — the exact thing this section exists to avoid. */}
          {tab === 'bulk' && (
            <div className="space-y-3">
              <SectionHead title="AI model error for every employee">
                Days the recognition pipeline failed to log anyone — a camera or ingest outage.
                Applies to every employee for {fmtMonth(month)}; edit the exceptions in the sheet
                afterwards.
              </SectionHead>

              {aiErrorEmployeeCount > 0 && (
                <p className="rounded-lg bg-muted/50 px-3 py-2 text-[12px] text-muted-foreground">
                  {aiErrorEmployeeCount} of {employeeCount || aiErrorEmployeeCount} employees
                  currently have an AI model error entered for this month.
                </p>
              )}

              <label className={`flex items-start gap-2.5 ${canEdit ? 'cursor-pointer' : ''}`}>
                <input
                  type="checkbox"
                  checked={bulkOn}
                  disabled={!canEdit}
                  onChange={(e) => setBulkOn(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-current text-brand disabled:opacity-60"
                />
                <span className="min-w-0 text-[13px] text-foreground">
                  Change AI model error for every employee
                  <span className="block text-[11px] text-muted-foreground">
                    Off by default — this one writes to every row in the sheet.
                  </span>
                </span>
              </label>

              {bulkOn && (
                // Indented under the checkbox it belongs to, and only rendered when it is on —
                // a disabled radio pair reads as broken rather than as not-yet-chosen.
                <div className="ml-6 space-y-2">
                  <label
                    className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-[13px] text-foreground
                      ${bulkMode === 'set' ? 'border-brand bg-brand/5' : 'border-border hover:bg-muted/40'}`}
                  >
                    <input
                      type="radio"
                      name="bulk-ai-error-mode"
                      checked={bulkMode === 'set'}
                      onChange={() => setBulkMode('set')}
                      className="h-3.5 w-3.5 shrink-0 accent-current text-brand"
                    />
                    <span>
                      Set it to
                      <input
                        type="number"
                        min={0}
                        max={workingDays || daysInMonth}
                        step={0.5}
                        value={bulkValue}
                        // Typing in the box selects the mode it belongs to, so the input is never a
                        // dead control while 'Clear' is chosen. On change rather than on focus:
                        // tabbing past it must not silently turn a chosen revoke back into a write.
                        onChange={(e) => { setBulkMode('set'); setBulkValue(e.target.value); }}
                        aria-label="AI model error days for every employee"
                        className={`mx-1.5 w-16 px-2 py-1 text-center ${inputBase}
                          ${bulkMode === 'set' && !setValueValid ? 'border-destructive' : 'border-border'}`}
                      />
                      day{Number(bulkValue) === 1 ? '' : 's'}
                    </span>
                  </label>
                  <label
                    className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-[13px] text-foreground
                      ${bulkMode === 'clear' ? 'border-brand bg-brand/5' : 'border-border hover:bg-muted/40'}`}
                  >
                    <input
                      type="radio"
                      name="bulk-ai-error-mode"
                      checked={bulkMode === 'clear'}
                      onChange={() => setBulkMode('clear')}
                      className="h-3.5 w-3.5 shrink-0 accent-current text-brand"
                    />
                    <span>Clear it — back to zero for everyone</span>
                  </label>

                  {/* The effect is spelled out because this is the one control here that writes to
                      every employee's row, and it OVERWRITES — a number already keyed in is
                      replaced. */}
                  {bulkMode === 'clear' ? (
                    <p className="flex items-start gap-1.5 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-[11px] text-foreground">
                      <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                      <span>
                        Sets AI model error to 0 on
                        {employeeCount ? ` all ${employeeCount} employees` : ' every employee'} for {fmtMonth(month)}.
                        {/* Clearing is not an undo, and saying so is the difference between a
                            revoke and a second mistake: individually-entered values go too, and
                            nothing here remembers what they were. */}
                        {aiErrorEmployeeCount > 0
                          ? ` The ${aiErrorEmployeeCount} value${aiErrorEmployeeCount === 1 ? '' : 's'} currently entered are removed, including any set per employee — this is not an undo, so anything typed in by hand has to be re-entered.`
                          : ' Nothing currently has a value, so this changes nothing.'}
                      </span>
                    </p>
                  ) : setValueValid ? (
                    <p className="flex items-start gap-1.5 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-[11px] text-foreground">
                      <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                      <span>
                        Overwrites AI model error on
                        {employeeCount ? ` all ${employeeCount} employees` : ' every employee'} for {fmtMonth(month)},
                        replacing anything already entered.
                      </span>
                    </p>
                  ) : (
                    <p className="text-[11px] text-destructive">
                      Enter a whole or half day between 0 and {workingDays || daysInMonth}.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Pinned summary + actions ──
            The formula first: it is the one line that describes the effect of every switch and
            credit above, and it belongs where the operator is deciding whether to commit. */}
        <div className="shrink-0 border-t border-border">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-5 py-2 text-[12px]">
            <span className="text-muted-foreground">Result:</span>
            <span className="font-medium text-foreground">{formula}</span>
            <span className="text-muted-foreground">
              · out of {workingDaysMode === 'derived' ? autoWorkingDays : (workingDaysValid ? parsedWorkingDays : workingDays)} working days
            </span>
          </div>

          {pendingChanges.length > 0 && (
            <div className="border-t border-border bg-brand/5">
              <button
                type="button"
                onClick={() => setChangesOpen((o) => !o)}
                aria-expanded={changesOpen}
                className="flex w-full items-center gap-2 px-5 py-2 text-left text-[12px] text-foreground hover:bg-brand/10"
              >
                <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${changesOpen ? '' : '-rotate-90'}`} />
                <span className="font-medium">
                  {pendingChanges.length} unsaved change{pendingChanges.length === 1 ? '' : 's'}
                </span>
                <span className="truncate text-muted-foreground">
                  {changesOpen ? '' : pendingChanges.map((c) => c.text).join(' · ')}
                </span>
              </button>
              {changesOpen && (
                <ul className="max-h-40 overflow-y-auto px-5 pb-2.5 text-[12px]">
                  {pendingChanges.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 py-0.5">
                      <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${c.warn ? 'bg-warning' : 'bg-brand'}`} />
                      <span className={c.warn ? 'text-foreground' : 'text-muted-foreground'}>{c.text}</span>
                      {/* Each line links back to where it was made, so a change you didn't mean
                          to make is one click from being undone. */}
                      <button
                        type="button"
                        onClick={() => setTab(c.tab)}
                        className="ml-auto shrink-0 text-[11px] text-brand hover:underline"
                      >
                        view
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && (
            <p className="flex items-start gap-1.5 border-t border-border bg-destructive/5 px-5 py-2 text-[12px] text-destructive">
              <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {error}
            </p>
          )}

          {/* The discard prompt replaces the normal actions rather than stacking a second dialog
              on top of this one — there is only one question outstanding at that point. */}
          {confirmDiscard ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
              <span className="mr-auto text-[12px] text-foreground">
                Discard {pendingChanges.length} unsaved change{pendingChanges.length === 1 ? '' : 's'}?
                Nothing has been saved yet.
              </span>
              <button
                onClick={() => setConfirmDiscard(false)}
                className={BTN_SECONDARY}
              >
                Keep editing
              </button>
              <button
                onClick={onClose}
                className={BTN_DANGER}
              >
                Discard
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
              {/* Says why Save is dead, rather than leaving a dimmed button to be puzzled over. */}
              {canEdit && !dirty && (
                <span className="mr-auto text-[12px] text-muted-foreground">No changes yet.</span>
              )}
              {canEdit && dirty && !canSubmit && (
                <span className="mr-auto text-[12px] text-destructive">
                  Fix the highlighted field before saving.
                </span>
              )}
              <button
                onClick={requestClose}
                className={BTN_SECONDARY}
              >
                {canEdit ? 'Cancel' : 'Close'}
              </button>
              {canEdit && (
                <button
                  onClick={submit}
                  disabled={!canSubmit || saving}
                  className={BTN_PRIMARY}
                >
                  {saving
                    ? 'Saving…'
                    : pendingChanges.length
                      ? `Save ${pendingChanges.length} change${pendingChanges.length === 1 ? '' : 's'}`
                      : 'Save setup'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
